import React, { useState, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar
} from '@mui/material';
import { Close as CloseIcon, Print as PrintIcon, Download as DownloadIcon, PictureAsPdf as PdfIcon, Code as HtmlIcon, Description as UblIcon } from '@mui/icons-material';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import JSZip from 'jszip';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';

interface EInvoiceDetailModalProps {
  open: boolean;
  onClose: () => void;
  ettn: string | null;
  companyId: string | null;
  invoiceNumber?: string;
  invoiceType?: 'sales' | 'purchase' | 'quick_sale'; // ✅ YENİ: Fatura tipi (SQL'den geliyor)
}

const EInvoiceDetailModal: React.FC<EInvoiceDetailModalProps> = ({
  open,
  onClose,
  ettn,
  companyId,
  invoiceNumber,
  invoiceType // ✅ YENİ: Fatura tipi
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Download menu state'leri
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const downloadMenuOpen = Boolean(downloadMenuAnchor);
  
  // Snackbar state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');

  // Faturadaki QR kod verisini kullanarak gerçek QR kod oluşturma
  const executeQRCodeScripts = () => {
    try {
      // QR kod oluşturuluyor
      
      // QR kod container'ını bul
      const qrcodeDiv = document.getElementById('qrcode');
      const qrvalueDiv = document.getElementById('qrvalue');
      
      if (!qrcodeDiv || !qrvalueDiv) {
        return;
      }
      
      // Faturadaki QR kod verisini al (JSON formatında)
      const qrData = qrvalueDiv.innerHTML.trim();
      
      // ETTN'yi çıkar (sadece log için)
      const ettnMatch = qrData.match(/"ettn":"([^"]+)"/);
      const currentEttn = ettnMatch ? ettnMatch[1] : ettn || 'N/A';
      
      // Container'ı temizle
      qrcodeDiv.innerHTML = '';
      
      // Gerçek QR kod oluştur
      createRealQRCode(qrcodeDiv, qrData, currentEttn);
      
      // QR kod başarıyla oluşturuldu
      
    } catch (error) {
      console.error('❌ QR kod genel hatası:', error);
      const qrcodeDiv = document.getElementById('qrcode');
      const qrvalueDiv = document.getElementById('qrvalue');
      const qrData = qrvalueDiv?.innerHTML.trim() || ettn || 'QR Kod Verisi Bulunamadı';
      if (qrcodeDiv) {
        showQRFallback(qrcodeDiv, qrData);
      }
    }
  };
  
  // Gerçek QR kod oluşturma (React Portal kullanarak)
  const createRealQRCode = (container: HTMLElement, qrData: string, ettnValue: string) => {
    try {
      // QR kod için wrapper div oluştur
      const qrWrapper = container.ownerDocument.createElement('div');
      qrWrapper.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      `;
      
      // QR kod container'ı oluştur
      const qrContainer = container.ownerDocument.createElement('div');
      qrContainer.id = 'qr-canvas-container';
      qrWrapper.appendChild(qrContainer);
      
      // ETTN bilgisi kaldırıldı
      
      container.appendChild(qrWrapper);
      
      // Faturadaki QR kod verisini kullanarak yüksek kaliteli QR kod oluştur
      const qrElement = React.createElement(QRCodeCanvas, {
        value: qrData, // qrvalue div'indeki JSON verisi
        size: 200, // Daha büyük boyut (140 -> 200)
        level: 'M', // Orta seviye hata düzeltme (daha iyi taranabilirlik)
        includeMargin: true,
        bgColor: '#FFFFFF',
        fgColor: '#000000',
        imageSettings: {
          src: '',
          height: 0,
          width: 0,
          excavate: false,
        }
        // Yüksek kalite ayarları
      });
      
      // Portal ile DOM'a ekle
      const portalRoot = container.ownerDocument.getElementById('qr-canvas-container');
      if (portalRoot) {
        // React 18 createRoot kullan
        import('react-dom/client').then(({ createRoot }) => {
          const root = createRoot(portalRoot);
          root.render(qrElement);
          // QR kod React Portal ile oluşturuldu
        }).catch(() => {
          // Fallback: React 17 render
          import('react-dom').then((ReactDOM) => {
            if ('render' in ReactDOM) {
              (ReactDOM as any).render(qrElement, portalRoot);
              // QR kod React 17 render ile oluşturuldu
            } else {
              console.error('❌ ReactDOM.render bulunamadı');
              showQRFallback(container, qrData);
            }
          }).catch((error) => {
            console.error('❌ React render hatası:', error);
            showQRFallback(container, qrData);
          });
        });
      }
      
    } catch (error) {
      console.error('❌ Gerçek QR kod oluşturma hatası:', error);
      showQRFallback(container, qrData);
    }
  };
  
  // Fallback QR kod gösterme
  const showQRFallback = (container: HTMLElement, qrData: string) => {
    container.innerHTML = `
      <div style="
        width: 200px;
        height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        text-align: center;
        background: #fafafa;
        color: #666;
        padding: 15px;
        box-sizing: border-box;
      ">
        <div style="font-size: 24px;">📱</div>
        <div style="margin-top: 10px;">QR Kod</div>
        <div>Yüklenemedi</div>
      </div>
    `;
  };


  // HTML'i modal'a uygun hale getiren fonksiyon
  const processHtmlForModal = (htmlContent: string): string => {
    // Gelen HTML'i olduğu gibi kullan, sadece sol boşluk ve karekod desteği için minimal CSS ekle
    const modalStyles = `
      <style>
        body { 
          margin: 0; 
          margin-left: 40px !important;
          margin-right: 20px !important;
          padding: 20px !important;
          background: white;
          overflow-x: auto;
        }
        /* Karekod ve resimler için güçlendirilmiş CSS */
        img {
          max-width: 100% !important;
          height: auto !important;
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
          border: none !important;
          background: transparent !important;
        }
        /* Base64 image desteği - QR kodlar için özel */
        img[src^="data:"] {
          display: inline-block !important;
          visibility: visible !important;
          opacity: 1 !important;
          min-width: 50px !important;
          min-height: 50px !important;
        }
        /* QR kod container'ları için */
        div[style*="qr"], .qr-code, [class*="qr"] {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
      </style>
    `;
    
    // Eğer HTML tam doküman değilse, minimal wrapper ekle
    if (!htmlContent.includes('<!DOCTYPE') && !htmlContent.includes('<html')) {
      return `
        <!DOCTYPE html>
        <html lang="tr">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          ${modalStyles}
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `;
    }
    
    // Tam HTML dokümanı ise, sadece charset ve minimal CSS ekle
    let processedHtml = htmlContent;
    
    // Head'e charset ve modal CSS'i ekle
    if (processedHtml.includes('<head>')) {
      processedHtml = processedHtml.replace('<head>', `<head><meta charset="UTF-8">${modalStyles}`);
    } else if (processedHtml.includes('<html>')) {
      processedHtml = processedHtml.replace('<html>', `<html><head><meta charset="UTF-8">${modalStyles}</head>`);
    }
    
    return processedHtml;
  };

  // Giden e-fatura detayını yükleme (GidenBelgeleriIndirEttn endpoint'i)
  const loadOutgoingInvoiceDetail = async (apiUrl: string, sqlData: any) => {
    if (!ettn) return;
    
    console.log('📤 [GİDEN E-FATURA] GidenBelgeleriIndirEttn çağrılıyor...', { ettn });
    
    // GidenBelgeleriIndirEttn endpoint'ini oluştur
    const endpoint = `${apiUrl}/QNBESolutions/GidenBelgeleriIndirEttn`;
    
    // Query parameters
    const params = new URLSearchParams({
      username: sqlData.username,
      password: sqlData.password,
      vkn: sqlData.taxNumber,
      belgeTuru: 'FATURA',
      belgeFormati: 'HTML' // HTML formatını iste
    });
    
    const fullUrl = `${endpoint}?${params.toString()}`;
    
    // POST request body - ETTN array
    const requestBody = [ettn];
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/zip,application/octet-stream,*/*',
      },
      body: JSON.stringify(requestBody),
      mode: 'cors',
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [GİDEN E-FATURA] API hatası:', errorText);
      throw new Error(`GidenBelgeleriIndirEttn hatası: ${response.status} - ${errorText}`);
    }
    
    console.log('✅ [GİDEN E-FATURA] Response alındı');
    
    // ZIP dosyası olarak gelir
    const arrayBuffer = await response.arrayBuffer();
    
    // ZIP'i aç ve HTML'i çıkar
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(arrayBuffer);
    
    // HTML dosyasını bul
    let htmlFile = null;
    for (const fileName in zipContent.files) {
      if (fileName.toLowerCase().endsWith('.html')) {
        htmlFile = zipContent.files[fileName];
        break;
      }
    }
    
    if (htmlFile) {
      const htmlContent = await htmlFile.async('text');
      console.log('✅ [GİDEN E-FATURA] HTML başarıyla çıkarıldı');
      const processedHtml = processHtmlForModal(htmlContent);
      setHtmlContent(processedHtml);
    } else {
      throw new Error('ZIP içinde HTML dosyası bulunamadı');
    }
  };

  const loadInvoiceDetail = async () => {
    if (!ettn || !companyId) return;
    
    setLoading(true);
    setError(null);
    setHtmlContent('');
    setPdfUrl(null);
    setIsPdf(false);
    
    try {
      console.log('📥 [E-INVOICE DETAIL] SQL\'den entegrasyon bilgileri yükleniyor...', { companyId });

      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        setError('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        setLoading(false);
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      console.log('✅ [E-INVOICE DETAIL] Entegrasyon bilgileri SQL\'den yüklendi');
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        setError('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        setLoading(false);
        return;
      }

      // ✅ YENİ: Fatura tipine göre doğru endpoint'i seç
      // SALES/QUICK_SALE = GİDEN e-fatura → GidenBelgeleriIndirEttn
      // PURCHASE = GELEN e-fatura → GetFaturaDetay
      if (invoiceType === 'sales' || invoiceType === 'quick_sale') {
        console.log('📤 [E-FATURA] Giden e-fatura tespit edildi, direkt GidenBelgeleriIndirEttn çağrılıyor...');
        await loadOutgoingInvoiceDetail(apiUrl, sqlData);
        return;
      }

      // GELEN e-fatura veya tip bilinmiyorsa GetFaturaDetay dene
      console.log('📥 [E-FATURA] Gelen e-fatura sorgusu yapılıyor (GetFaturaDetay)...');
      
      // API endpoint'ini oluştur (POST method ile query parameters)
      const endpoint = `${apiUrl}/QNBESolutions/GetFaturaDetay`;
      
       // HTML formatını kullan (byte array olarak gelecek)
       const params = new URLSearchParams({
         username: sqlData.username,
         password: sqlData.password, // Plain text (backend'de decrypt edilmiş)
         vkn: sqlData.taxNumber,
         ettn: ettn,
         format: 'HTML' // HTML formatını kullan
       });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      // HTML formatı ile API çağrısı yap (byte array response kabul et)
      let response;
      try {
        response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json,application/octet-stream,text/html,*/*',
          },
          mode: 'cors',
        });
      } catch (fetchError) {
        // Network hatası - giden fatura olarak dene
        console.log('ℹ️ [E-FATURA] Gelen fatura sorgusu başarısız, giden fatura deneniyor...');
        await loadOutgoingInvoiceDetail(apiUrl, sqlData);
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        
        // ✅ Eğer 400 hatası ise, bu GİDEN e-fatura olabilir, GidenBelgeleriIndirEttn dene
        if (response.status === 400 && errorText.includes('Girilen kriterlere uygun faturalar bulunamadı')) {
          console.log('ℹ️ [E-FATURA] Bu bir giden e-fatura, alternatif endpoint deneniyor...');
          try {
            await loadOutgoingInvoiceDetail(apiUrl, sqlData);
            return; // Başarılı olduysa burada çık
          } catch (outgoingError) {
            console.error('❌ [E-FATURA] Her iki endpoint de başarısız:', outgoingError);
            // Her iki endpoint de başarısız, kullanıcıya anlamlı hata göster
            throw new Error('Bu ETTN ile gelen veya giden e-fatura bulunamadı. ETTN\'yi kontrol edin.');
          }
        }
        
        // Diğer 400 dışı hatalar için orijinal hatayı fırlat
        console.error('❌ [E-FATURA] GetFaturaDetay API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Response'un content-type'ını kontrol et
      const contentType = response.headers.get('content-type');
      
      // Binary response ise (byte array olarak HTML)
      if (contentType && contentType.includes('application/octet-stream')) {
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        
        try {
          // Byte array'i UTF-8 string'e çevir
          const decoder = new TextDecoder('utf-8');
          const htmlContent = decoder.decode(uint8Array);
          
          // Byte array başarıyla HTML'ye çevrildi
          
          setHtmlContent(htmlContent);
          return;
        } catch (decodeError) {
          console.error('Byte array decode hatası:', decodeError);
          
          // UTF-8 başarısız ise Latin1 dene
          try {
            const decoder = new TextDecoder('latin1');
            const htmlContent = decoder.decode(uint8Array);
            
            // Byte array Latin1 ile HTML'ye çevrildi
            setHtmlContent(htmlContent);
            return;
          } catch (latin1Error) {
            console.error('Latin1 decode hatası:', latin1Error);
            setError('Byte array HTML\'ye çevrilemedi: ' + (decodeError instanceof Error ? decodeError.message : String(decodeError)));
            return;
          }
        }
      }
      // PDF response ise
      else if (contentType && contentType.includes('application/pdf')) {
        
        const arrayBuffer = await response.arrayBuffer();
              const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
              const newPdfUrl = URL.createObjectURL(blob);
              setPdfUrl(newPdfUrl);
              setIsPdf(true);
              
              // PDF başarıyla yüklendi
        return;
      }
      // Binary response ise (ZIP dosyası)
      else if (contentType && (contentType.includes('application/octet-stream') || contentType.includes('application/zip'))) {
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        
        try {
          const zip = new JSZip();
          const zipContent = await zip.loadAsync(arrayBuffer);
          
          // HTML dosyasını bul
          let htmlFile = null;
          for (const fileName in zipContent.files) {
            if (fileName.toLowerCase().endsWith('.html')) {
              htmlFile = zipContent.files[fileName];
              break;
            }
          }
          
          if (htmlFile) {
            const htmlContent = await htmlFile.async('text');
            setHtmlContent(htmlContent);
            return;
          } else {
            // HTML dosyası bulunamazsa ilk dosyayı al
            const firstFileName = Object.keys(zipContent.files)[0];
            if (firstFileName) {
              const firstFile = zipContent.files[firstFileName];
              const htmlContent = await firstFile.async('text');
              setHtmlContent(htmlContent);
              return;
            } else {
              setError('Binary ZIP dosyası boş.');
              return;
            }
          }
        } catch (binaryZipError) {
          console.error('Binary ZIP açılırken hata:', binaryZipError);
          setError('Binary ZIP dosyası açılamadı: ' + (binaryZipError instanceof Error ? binaryZipError.message : String(binaryZipError)));
          return;
        }
      }
      // JSON response ise
      else if (contentType && contentType.includes('application/json')) {
        const jsonData = await response.json();
        
        // JSON içinde content alanı varsa onu kullan
        if (jsonData.content) {
          const content = jsonData.content;
          
          // HTML formatında byte array ise
          if (jsonData.format === 'HTML' && Array.isArray(content)) {
            try {
              // Array'i Uint8Array'e çevir
              const uint8Array = new Uint8Array(content);
              
              // Byte array'i UTF-8 string'e çevir
              const decoder = new TextDecoder('utf-8');
              const htmlContent = decoder.decode(uint8Array);
              
              setHtmlContent(htmlContent);
              return;
            } catch (arrayDecodeError) {
              console.error('JSON byte array decode hatası:', arrayDecodeError);
              
              // UTF-8 başarısız ise Latin1 dene
              try {
                const uint8Array = new Uint8Array(content);
                const decoder = new TextDecoder('latin1');
                const htmlContent = decoder.decode(uint8Array);
                setHtmlContent(htmlContent);
                return;
              } catch (latin1ArrayError) {
                console.error('JSON byte array Latin1 decode hatası:', latin1ArrayError);
                setError('JSON byte array HTML\'ye çevrilemedi: ' + (arrayDecodeError instanceof Error ? arrayDecodeError.message : String(arrayDecodeError)));
                return;
              }
            }
          }
          // PDF formatı ise
          else if (jsonData.format === 'PDF' && typeof content === 'string') {
            try {
              // Base64 decode et
              const binaryString = atob(content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              const blob = new Blob([bytes], { type: 'application/pdf' });
              const newPdfUrl = URL.createObjectURL(blob);
              setPdfUrl(newPdfUrl);
              setIsPdf(true);
              
              return;
            } catch (pdfError) {
              console.error('PDF decode hatası:', pdfError);
              setError('PDF dosyası açılamadı: ' + (pdfError instanceof Error ? pdfError.message : String(pdfError)));
              return;
            }
          }
          // İlk olarak direkt HTML olup olmadığını kontrol et
          else if (typeof content === 'string' && (content.includes('<html') || content.includes('<!DOCTYPE'))) {
            const processedHtml = processHtmlForModal(content);
            setHtmlContent(processedHtml);
          }
          // Content'in ZIP dosyası olup olmadığını kontrol et (PK ile başlıyorsa)
          else if (typeof content === 'string' && content.startsWith('PK')) {
             
             try {
               // Farklı decode yöntemlerini dene
               let bytes: Uint8Array;
               let decodeMethod = '';
               
               // Yöntem 1: Latin1 encoding ile binary string decode
               try {
                 const binaryString = content;
                 bytes = new Uint8Array(binaryString.length);
                 for (let i = 0; i < binaryString.length; i++) {
                   bytes[i] = binaryString.charCodeAt(i) & 0xFF;
                 }
                 decodeMethod = 'Latin1 Binary String';
               } catch (latin1Error) {
                 // Yöntem 2: Base64 decode dene
                 try {
                   bytes = Uint8Array.from(atob(content), c => c.charCodeAt(0));
                   decodeMethod = 'Base64';
                 } catch (base64Error) {
                   // Yöntem 3: TextEncoder ile ArrayBuffer'a çevir
                   try {
                     const encoder = new TextEncoder();
                     const decoder = new TextDecoder('latin1');
                     const decodedString = decoder.decode(new Uint8Array(Array.from(content, c => c.charCodeAt(0))));
                     bytes = encoder.encode(decodedString);
                     decodeMethod = 'TextEncoder/Decoder';
                   } catch (textError) {
                     // Yöntem 4: Raw binary (son çare)
                     bytes = new Uint8Array(content.length);
                     for (let i = 0; i < content.length; i++) {
                       bytes[i] = content.charCodeAt(i) & 0xFF;
                     }
                     decodeMethod = 'Raw Binary (fallback)';
                   }
                 }
               }
               
               const zip = new JSZip();
               
               // JSZip'e farklı formatlarla yüklemeyi dene
               let zipContent;
               try {
                 // Önce Uint8Array olarak dene
                 zipContent = await zip.loadAsync(bytes);
               } catch (uint8Error) {
                 try {
                   // ArrayBuffer olarak dene
                   const arrayBuffer = bytes.buffer as ArrayBuffer;
                   zipContent = await zip.loadAsync(arrayBuffer);
                 } catch (bufferError) {
                   try {
                     // Raw string olarak dene (son çare)
                     zipContent = await zip.loadAsync(content, {base64: false});
                   } catch (stringError) {
                     throw new Error(`ZIP yükleme başarısız: Uint8Array(${uint8Error instanceof Error ? uint8Error.message : String(uint8Error)}), ArrayBuffer(${bufferError instanceof Error ? bufferError.message : String(bufferError)}), String(${stringError instanceof Error ? stringError.message : String(stringError)})`);
                   }
                 }
               }
              
              // HTML dosyasını bul
              let htmlFile = null;
              for (const fileName in zipContent.files) {
                if (fileName.toLowerCase().endsWith('.html')) {
                  htmlFile = zipContent.files[fileName];
                  break;
                }
              }
              
              if (htmlFile) {
                const htmlContent = await htmlFile.async('text');
                const processedHtml = processHtmlForModal(htmlContent);
                setHtmlContent(processedHtml);
              } else {
                // HTML dosyası bulunamazsa ilk dosyayı al
                const firstFileName = Object.keys(zipContent.files)[0];
                if (firstFileName) {
                  const firstFile = zipContent.files[firstFileName];
                  const htmlContent = await firstFile.async('text');
                  const processedHtml = processHtmlForModal(htmlContent);
                  setHtmlContent(processedHtml);
                } else {
                  setError('ZIP dosyası boş veya okunamıyor.');
                }
              }
             } catch (zipError) {
               console.error('JSON içindeki ZIP dosyası açılırken hata:', zipError);
               setHtmlContent(`
                 <div style="padding: 20px; font-family: Arial, sans-serif;">
                   <h3>⚠️ ZIP Dosyası Açılamadı</h3>
                   <p><strong>Hata:</strong> ${zipError instanceof Error ? zipError.message : String(zipError)}</p>
                   <p><strong>ETTN:</strong> ${ettn}</p>
                   <p><strong>Dosya Boyutu:</strong> ${content.length} byte</p>
                   <details>
                     <summary>Raw Content (İlk 1000 karakter)</summary>
                     <pre style="background: #f5f5f5; padding: 10px; overflow: auto; max-height: 300px;">${content.substring(0, 1000)}</pre>
                   </details>
                 </div>
               `);
               return;
             }
          } else {
            // Base64 decode edilmiş HTML içerik olabilir
            if (jsonData.format === 'HTML' && typeof content === 'string') {
              try {
                // Base64 decode et
                const binaryString = atob(content);
                
                // Türkçe karakter desteği için UTF-8 decode
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                  bytes[i] = binaryString.charCodeAt(i);
                }
                
                // UTF-8 TextDecoder kullan
                const decoder = new TextDecoder('utf-8');
                const decodedContent = decoder.decode(bytes);
                
                
                // QR kod debug logları kaldırıldı
                
                // HTML'i modal'a uygun hale getir
                const processedHtml = processHtmlForModal(decodedContent);
                setHtmlContent(processedHtml);
                
              } catch (decodeError) {
                console.error('Base64 decode hatası:', decodeError);
                try {
                  // Fallback: Direkt atob kullan
                  const decodedContent = atob(content);
                  const processedHtml = processHtmlForModal(decodedContent);
                  setHtmlContent(processedHtml);
                } catch (fallbackError) {
                  console.error('Fallback decode hatası:', fallbackError);
                  setHtmlContent(content);
                }
              }
            } else {
              // Direkt HTML content
              const processedHtml = processHtmlForModal(content);
              setHtmlContent(processedHtml);
            }
          }
        } else {
          // JSON'un kendisini string olarak göster
          setHtmlContent(`<pre>${JSON.stringify(jsonData, null, 2)}</pre>`);
        }
      } else {
        // Normal text response (e.g., direct HTML)
        const textData = await response.text();
        setHtmlContent(textData);
      }
      
      // Fatura detayı başarıyla yüklendi
      
    } catch (err) {
      console.error('Fatura detayı yüklenirken hata (POST with query):', err);
      if (err instanceof Error && err.message.includes('CORS')) {
        setError('CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor. Lütfen sistem yöneticisine başvurun veya API sunucusunda CORS ayarlarını kontrol edin.');
      } else if (err instanceof Error && err.message.includes('Failed to fetch')) {
        setError('Bağlantı hatası: API sunucusuna POST isteği (query parameters ile) gönderilemedi. Lütfen API sunucusunun çalıştığından ve ağ bağlantınızın aktif olduğundan emin olun.');
      } else {
        setError(err instanceof Error ? err.message : 'Fatura detayı yüklenirken bir hata oluştu (POST with query).');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && ettn && companyId) {
      loadInvoiceDetail();
    }
  }, [open, ettn, companyId]);

  const handlePrint = () => {
    if (!htmlContent) return;
    
    try {
      // Yeni pencerede HTML'i aç ve yazdır
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Popup penceresi engellendi. Lütfen popup engelleyiciyi devre dışı bırakın.');
        return;
      }
      
      // Yazdırma için optimize edilmiş HTML oluştur
      const printHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>E-Fatura ${ettn || 'Detay'}</title>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              margin: 0;
              padding: 10mm;
              font-family: Arial, sans-serif;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              background: white;
              width: auto;
              min-width: 900px;
              box-sizing: border-box;
              overflow-x: visible;
            }
            * {
              box-sizing: border-box;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
            }
            td, th {
              padding: 4px;
              border: 1px solid #000;
              font-size: 11px;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            .no-break {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          ${htmlContent.replace(/<body[^>]*>|<\/body>/gi, '').replace(/<html[^>]*>|<\/html>/gi, '').replace(/<head[^>]*>.*?<\/head>/gi, '')}
          
        </body>
        </html>
      `;
      
      printWindow.document.write(printHtml);
      printWindow.document.close();
      
      // Otomatik yazdırma dialog'unu aç
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      };
      
    } catch (error) {
      console.error('Yazdırma hatası:', error);
      alert('Yazdırma sırasında bir hata oluştu.');
    }
  };

  const handleDownloadPdf = async () => {
    if (!ettn || !companyId) return;
    
    try {
      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        alert('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        return;
      }

      // ✅ YENİ: Fatura tipine göre doğru endpoint'i seç
      // SALES/QUICK_SALE = GİDEN e-fatura → GidenBelgeleriIndirEttn
      // PURCHASE = GELEN e-fatura → GelenBelgeleriIndir
      const isOutgoing = invoiceType === 'sales' || invoiceType === 'quick_sale';
      const endpoint = isOutgoing
        ? `${apiUrl}/QNBESolutions/GidenBelgeleriIndirEttn` // Giden e-fatura
        : `${apiUrl}/QNBESolutions/GelenBelgeleriIndir`; // Gelen e-fatura
      
      // Query parameters (giden ve gelen için farklı)
      const params = isOutgoing
        ? new URLSearchParams({
            username: sqlData.username,
            password: sqlData.password,
            vkn: sqlData.taxNumber,
            belgeTuru: 'FATURA',
            belgeFormati: 'PDF' // Giden için belgeFormati
          })
        : new URLSearchParams({
            username: sqlData.username,
            password: sqlData.password,
            vkn: sqlData.taxNumber,
            format: 'PDF', // Gelen için format
            belgeTuru: 'FATURA'
          });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      // POST request body - ETTN array
      const requestBody = [ettn];
      
      console.log(`📥 [${isOutgoing ? 'GİDEN' : 'GELEN'} E-FATURA PDF] İndirme isteği gönderiliyor:`, {
        endpoint: endpoint,
        params: isOutgoing 
          ? {
              username: sqlData.username,
              password: '***',
              vkn: sqlData.taxNumber,
              belgeTuru: 'FATURA',
              belgeFormati: 'PDF'
            }
          : {
              username: sqlData.username,
              password: '***',
              vkn: sqlData.taxNumber,
              format: 'PDF',
              belgeTuru: 'FATURA'
            },
        body: requestBody
      });
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/pdf,application/octet-stream',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF indirme API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // File response'unu işle
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      const arrayBuffer = await response.arrayBuffer();
      console.log('Dosya boyutu:', arrayBuffer.byteLength, 'bytes');
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('Boş dosya alındı');
      }
      
      // ✅ YENİ: Giden e-fatura ise ZIP dosyası gelir, içinden PDF'i çıkar
      if (isOutgoing && (contentType?.includes('application/zip') || contentType?.includes('application/octet-stream'))) {
        console.log('📦 [GİDEN E-FATURA PDF] ZIP dosyası açılıyor...');
        
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(arrayBuffer);
        
        // PDF dosyasını bul
        let pdfFile = null;
        for (const fileName in zipContent.files) {
          if (fileName.toLowerCase().endsWith('.pdf')) {
            pdfFile = zipContent.files[fileName];
            break;
          }
        }
        
        if (!pdfFile) {
          throw new Error('ZIP içinde PDF dosyası bulunamadı');
        }
        
        const pdfArrayBuffer = await pdfFile.async('arraybuffer');
        const blob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ [GİDEN E-FATURA PDF] PDF başarıyla indirildi');
      } else {
        // Gelen e-fatura → Direkt binary PDF dosyası
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ [GELEN E-FATURA PDF] PDF başarıyla indirildi');
      }
      
    } catch (error) {
      console.error('PDF indirme hatası:', error);
      
      // ✅ IDM/Download Manager müdahale ederse "Failed to fetch" gelir ama indirme başarılı olabilir
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        console.warn('⚠️ [PDF İNDİRME] Network hatası - IDM/Download Manager müdahale etmiş olabilir');
        setSnackbarMessage('📥 PDF indirme başlatıldı. İndirmeler klasörünüzü kontrol edin.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return; // Hata fırlatma, çünkü indirme muhtemelen başarılı
      }
      
      // Diğer hatalar
      if (error instanceof Error && error.message.includes('CORS')) {
        setSnackbarMessage('❌ CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('❌ PDF indirme hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleDownloadHtml = () => {
    if (!htmlContent) return;
    
    try {
      // HTML dosyası olarak indir
      const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `e-fatura-${ettn || 'detay'}-${new Date().toISOString().split('T')[0]}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('HTML başarıyla indirildi:', `e-fatura-${ettn || 'detay'}-${new Date().toISOString().split('T')[0]}.html`);
    } catch (error) {
      console.error('HTML indirme hatası:', error);
      alert('HTML dosyası indirme sırasında bir hata oluştu.');
    }
  };

  const handleDownloadUbl = async () => {
    if (!ettn || !companyId) return;
    
    try {
      // ✅ SQL API'den entegrasyon bilgilerini çek
      const integrationResponse = await accountingApi.get(ACCOUNTING_API.EINVOICE_INTEGRATION_GET);
      const result = integrationResponse.data;
      const sqlData = result.data;

      if (!sqlData) {
        alert('E-fatura entegrasyon bilgileri bulunamadı. Lütfen önce entegrasyon ayarlarını yapın.');
        return;
      }

      // ✅ Backend'den plain text gelir (HTTPS ile güvenli)
      
      // API URL'sini al
      const apiUrl = process.env.REACT_APP_EINVOICE_URL;
      if (!apiUrl) {
        alert('E-fatura API URL\'si bulunamadı. Lütfen sistem yöneticisine başvurun.');
        return;
      }

      // ✅ YENİ: Fatura tipine göre doğru endpoint'i seç
      const isOutgoing = invoiceType === 'sales' || invoiceType === 'quick_sale';
      const endpoint = isOutgoing
        ? `${apiUrl}/QNBESolutions/GidenBelgeleriIndirEttn`
        : `${apiUrl}/QNBESolutions/GelenBelgeleriIndir`;
      
      // Query parameters (giden ve gelen için farklı)
      const params = isOutgoing
        ? new URLSearchParams({
            username: sqlData.username,
            password: sqlData.password,
            vkn: sqlData.taxNumber,
            belgeTuru: 'FATURA',
            belgeFormati: 'UBL' // Giden için belgeFormati
          })
        : new URLSearchParams({
            username: sqlData.username,
            password: sqlData.password,
            vkn: sqlData.taxNumber,
            format: 'UBL', // Gelen için format
            belgeTuru: 'FATURA'
          });

      const fullUrl = `${endpoint}?${params.toString()}`;
      
      // POST request body - ETTN array
      const requestBody = [ettn];
      
      console.log(`📥 [${isOutgoing ? 'GİDEN' : 'GELEN'} E-FATURA UBL] İndirme isteği gönderiliyor:`, {
        endpoint: endpoint,
        params: isOutgoing 
          ? {
              username: sqlData.username,
              password: '***',
              vkn: sqlData.taxNumber,
              belgeTuru: 'FATURA',
              belgeFormati: 'UBL'
            }
          : {
              username: sqlData.username,
              password: '***',
              vkn: sqlData.taxNumber,
              format: 'UBL',
              belgeTuru: 'FATURA'
            },
        body: requestBody
      });
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/xml,application/octet-stream,*/*',
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('UBL indirme API hatası:', errorText);
        throw new Error(`API hatası: ${response.status} - ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      // File response'unu işle
      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);
      
      const arrayBuffer = await response.arrayBuffer();
      console.log('Dosya boyutu:', arrayBuffer.byteLength, 'bytes');
      
      if (arrayBuffer.byteLength === 0) {
        throw new Error('Boş dosya alındı');
      }
      
      // ✅ YENİ: Giden e-fatura ise ZIP dosyası gelir, içinden XML'i çıkar
      if (isOutgoing && (contentType?.includes('application/zip') || contentType?.includes('application/octet-stream'))) {
        console.log('📦 [GİDEN E-FATURA UBL] ZIP dosyası açılıyor...');
        
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(arrayBuffer);
        
        // XML dosyasını bul
        let xmlFile = null;
        for (const fileName in zipContent.files) {
          if (fileName.toLowerCase().endsWith('.xml')) {
            xmlFile = zipContent.files[fileName];
            break;
          }
        }
        
        if (!xmlFile) {
          throw new Error('ZIP içinde XML dosyası bulunamadı');
        }
        
        const xmlArrayBuffer = await xmlFile.async('arraybuffer');
        const blob = new Blob([xmlArrayBuffer], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ [GİDEN E-FATURA UBL] XML başarıyla indirildi');
      } else {
        // Gelen e-fatura → Direkt binary XML dosyası
        const blob = new Blob([arrayBuffer], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        
        const a = document.createElement('a');
        a.href = url;
        a.download = `e-fatura-${ettn}-${new Date().toISOString().split('T')[0]}.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        console.log('✅ [GELEN E-FATURA UBL] XML başarıyla indirildi');
      }
      
    } catch (error) {
      console.error('UBL indirme hatası:', error);
      
      // ✅ IDM/Download Manager müdahale ederse "Failed to fetch" gelir ama indirme başarılı olabilir
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        console.warn('⚠️ [UBL İNDİRME] Network hatası - IDM/Download Manager müdahale etmiş olabilir');
        setSnackbarMessage('📥 UBL/XML indirme başlatıldı. İndirmeler klasörünüzü kontrol edin.');
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
        return; // Hata fırlatma, çünkü indirme muhtemelen başarılı
      }
      
      // Diğer hatalar
      if (error instanceof Error && error.message.includes('CORS')) {
        setSnackbarMessage('❌ CORS hatası: API sunucusu bu domain\'den POST isteklere izin vermiyor.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('❌ UBL indirme hatası: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // Download menu işlemleri
  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownloadPdfClick = () => {
    handleDownloadMenuClose();
    handleDownloadPdf();
  };

  const handleDownloadHtmlClick = () => {
    handleDownloadMenuClose();
    handleDownloadHtml();
  };

  const handleDownloadUblClick = () => {
    handleDownloadMenuClose();
    handleDownloadUbl();
  };

  const handleClose = () => {
    setHtmlContent('');
    setError(null);
    // PDF URL'sini temizle
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setIsPdf(false);
    onClose();
  };

  
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      disableScrollLock={true}
      PaperProps={{
        sx: { 
          height: '90vh',
          width: '70vw',
          maxWidth: '900px',
          zIndex: 9999,
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      sx={{
        zIndex: 9999,
        display: 'flex !important',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998
        },
        '& .MuiDialog-container': {
          display: 'flex !important',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {tt('E-Fatura Detayı', 'E-Invoice Detail')}
            {invoiceNumber && ` - ${invoiceNumber}`}
          </Typography>
          <Box>
            {htmlContent && !loading && (
              <>
                <Tooltip title={tt('Yazdır', 'Print')}>
                  <IconButton onClick={handlePrint} size="small">
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tt('İndir', 'Download')}>
                  <IconButton onClick={handleDownloadMenuOpen} size="small">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {ettn && (
          <Typography variant="body2" color="text.secondary">
            ETTN: {ettn}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {tt('E-fatura detayı yükleniyor...', 'Loading e-invoice detail...')}
            </Typography>
          </Box>
        )}
        
        {error && (
          <Box p={2}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        )}
        
        {!loading && !error && (
          isPdf ? (
            pdfUrl && <iframe
                src={pdfUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={tt('E-Fatura PDF', 'E-Invoice PDF')}
            />
          ) : (
            htmlContent && <iframe
                srcDoc={htmlContent}
                style={{ width: '100%', height: '100%', border: 'none' }}
                title={tt('E-Fatura Detayı', 'E-Invoice Detail')}
                ref={iframeRef}
                onLoad={executeQRCodeScripts}
            />
          )
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Download Menu - Dialog dışında */}
    <Menu
      anchorEl={downloadMenuAnchor}
      open={downloadMenuOpen}
      onClose={handleDownloadMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        zIndex: 10000, // Modal'dan daha yüksek z-index
        '& .MuiPaper-root': {
          minWidth: 150,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }
      }}
    >
      <MenuItem onClick={handleDownloadPdfClick}>
        <ListItemIcon>
          <PdfIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{tt('PDF İndir', 'Download PDF')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleDownloadHtmlClick}>
        <ListItemIcon>
          <HtmlIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{tt('HTML İndir', 'Download HTML')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleDownloadUblClick}>
        <ListItemIcon>
          <UblIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{tt('UBL İndir', 'Download UBL')}</ListItemText>
      </MenuItem>
    </Menu>

    {/* Snackbar - Bildirimler için */}
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={snackbarSeverity === 'error' ? 6000 : 4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert 
        onClose={() => setSnackbarOpen(false)} 
        severity={snackbarSeverity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbarMessage}
      </Alert>
    </Snackbar>
  </>
  );
};

export default memo(EInvoiceDetailModal);